import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

const bar: React.CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 60,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: "10px 16px calc(10px + env(safe-area-inset-bottom))",
  fontFamily: "var(--rc-font)",
  fontSize: 14,
};

/** How often an open app asks the server whether a new version exists (a browser only checks when a page is opened). */
const UPDATE_CHECK_MS = 15 * 60 * 1000;
/** How often a waiting update is applied if nothing has been typed on the page. */
const APPLY_CHECK_MS = 30 * 1000;

/** Install-app plumbing: registers the service worker, applies a new version by itself when nothing on the page would be lost
 *  (offering a Reload instead once something has been typed), and says so when the device is offline (the shell loads offline, the data does not). */
export function PwaStatus() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const registration = useRef<ServiceWorkerRegistration | undefined>(undefined);
  // True once anything has been typed or changed on this page: a reload would throw that away, so the update then waits for a click.
  const [edited, setEdited] = useState(false);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, r) {
      registration.current = r;
      if (r) setInterval(() => void r.update(), UPDATE_CHECK_MS);
    },
  });

  useEffect(() => {
    const touch = () => setEdited(true);
    const check = () => {
      if (document.visibilityState === "visible") void registration.current?.update();
    };
    document.addEventListener("input", touch, true);
    document.addEventListener("change", touch, true);
    document.addEventListener("visibilitychange", check);
    return () => {
      document.removeEventListener("input", touch, true);
      document.removeEventListener("change", touch, true);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  // A waiting new version is applied straight away unless something has been typed here. Checked when it arrives, whenever the app
  // is switched away from or back to, and every 30 seconds, so an app left open picks it up too.
  useEffect(() => {
    if (!needRefresh) return;
    if (edited) return;
    const apply = () => void updateServiceWorker(true);
    apply();
    const timer = setInterval(apply, APPLY_CHECK_MS);
    document.addEventListener("visibilitychange", apply);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", apply);
    };
  }, [needRefresh, edited, updateServiceWorker]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  // The public booking page never shows the banner to a customer: a waiting version is applied by the effect above as soon as nothing has
  // been typed there, and left alone while they are filling in the form.
  const onBookingPage = window.location.pathname.startsWith("/book/");
  if (needRefresh && edited && !onBookingPage) {
    return (
      <div role="status" style={{ ...bar, background: "#0c2a44", color: "#fff", borderTop: "1px solid rgba(56, 189, 248, 0.5)" }}>
        A new version is available.
        <button onClick={() => void updateServiceWorker(true)} style={{ padding: "6px 14px", border: "none", borderRadius: 6, background: "#38bdf8", color: "#04202f", fontWeight: 600, cursor: "pointer" }}>
          Reload
        </button>
      </div>
    );
  }
  if (!online) {
    return (
      <div role="status" style={{ ...bar, background: "#92600a", color: "#fff" }}>
        You're offline. Calls and bookings will load when you're back online.
      </div>
    );
  }
  return null;
}
